// <load-more> custom element. Watches itself with an IntersectionObserver and
// auto-loads the next page of products via Shopify's Section Rendering API
// when it scrolls into view. Inserts skeleton placeholder cards during the
// fetch so the grid never looks empty. Falls back to a "Load more" button
// click if JS is degraded (no IO support) or the user keyboard-tabs into it.
//
// Required attributes:
//   section-id        — the section id (used as ?section_id=... in the fetch)
//   next-url          — full URL of the next page (from Liquid's paginate.next.url)
//   next-batch-size   — how many skeleton cards to render while fetching

(function () {
  if (customElements.get('load-more')) return;

  // Skeleton visuals come from the shared hc-skel system loaded globally via
  // theme/assets/hc-skeleton.css. See docs/SKELETONS.md for the contract.
  injectStyles();

  class LoadMore extends HTMLElement {
    connectedCallback() {
      this._button = this.querySelector('button');
      this._loading = false;
      this._exhausted = false;

      if (this._button) {
        this._button.addEventListener('click', () => this._loadNext());
      }

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting && !this._loading && !this._exhausted) {
                this._loadNext();
              }
            }
          },
          { rootMargin: '600px 0px' }
        );
        this._io.observe(this);
      }
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
    }

    _productList() {
      const sectionId = this.getAttribute('section-id');
      return document
        .getElementById(`shopify-section-${sectionId}`)
        ?.querySelector('product-list');
    }

    _renderSkeletons(count) {
      const list = this._productList();
      if (!list) return [];
      const skeletons = [];
      for (let i = 0; i < count; i++) {
        const card = document.createElement('div');
        card.className = 'hc-skel-product-card';
        card.setAttribute('aria-hidden', 'true');
        card.innerHTML =
          '<div class="hc-skel hc-skel--media-1x1"></div>' +
          '<div class="hc-skel hc-skel--text-lg"></div>' +
          '<div class="hc-skel hc-skel--text-sm"></div>';
        list.appendChild(card);
        skeletons.push(card);
      }
      return skeletons;
    }

    async _loadNext() {
      if (this._loading || this._exhausted) return;
      const sectionId = this.getAttribute('section-id');
      const nextUrl = this.getAttribute('next-url');
      const batchSize = parseInt(this.getAttribute('next-batch-size') || '24', 10);
      if (!sectionId || !nextUrl) return;

      const productList = this._productList();
      if (!productList) return;

      this._loading = true;
      productList.setAttribute('aria-busy', 'true');
      if (this._button) this._button.disabled = true;
      this.classList.add('is-loading');

      const skeletons = this._renderSkeletons(batchSize);

      try {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', sectionId);
        const res = await fetch(url.toString(), { headers: { 'X-Requested-With': 'fetch' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();

        const tmp = document.createElement('div');
        tmp.innerHTML = html;

        const incomingList = tmp.querySelector('product-list');
        if (!incomingList) throw new Error('product-list not found in response');

        // Remove skeletons before appending real cards
        for (const sk of skeletons) sk.remove();

        const fragment = document.createDocumentFragment();
        for (const child of Array.from(incomingList.children)) {
          fragment.appendChild(child);
        }
        productList.appendChild(fragment);

        const incomingLoadMore = tmp.querySelector('load-more');
        if (incomingLoadMore && incomingLoadMore.getAttribute('next-url')) {
          this.setAttribute('next-url', incomingLoadMore.getAttribute('next-url'));
        } else {
          this._exhausted = true;
          if (this._io) this._io.disconnect();
          this.remove();
          return;
        }
      } catch (e) {
        console.error('[load-more]', e);
        for (const sk of skeletons) sk.remove();
        // Surface the button so the user has a manual fallback
        this.classList.add('has-error');
      } finally {
        this._loading = false;
        productList.removeAttribute('aria-busy');
        if (this._button) this._button.disabled = false;
        this.classList.remove('is-loading');
      }
    }
  }

  customElements.define('load-more', LoadMore);

  // Only the manual-button fallback styling lives here. Skeleton visuals are
  // owned by the shared hc-skeleton.css (loaded globally from theme.liquid).
  function injectStyles() {
    if (document.getElementById('load-more-styles')) return;
    const style = document.createElement('style');
    style.id = 'load-more-styles';
    style.textContent = `
      .collection__load-more {
        display: flex;
        justify-content: center;
        margin-block-start: var(--spacing-6, 1.5rem);
      }
      /* Hide the manual button by default; only revealed on JS error
         or when the user explicitly tabs onto it (keyboard fallback). */
      .collection__load-more .load-more__button {
        position: absolute;
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        height: 1px;
        width: 1px;
        overflow: hidden;
        white-space: nowrap;
      }
      .collection__load-more .load-more__button:focus-visible,
      .collection__load-more.has-error .load-more__button {
        position: static;
        clip: auto;
        clip-path: none;
        height: auto;
        width: auto;
        overflow: visible;
        white-space: normal;
      }
    `;
    document.head.appendChild(style);
  }
})();
